import type { InvoiceResponse } from '../../api/types';
import {
  attachInvoicePdf,
  getInvoicePdf,
  type InvoicePdfVariant,
} from '../../api/endpoints/invoicePdfs';
import { completeUploadIntent, createUploadIntent } from '../../api/endpoints/uploads';

type Uploaded = { intentId: string; sha256: string };
type PdfState = 'PREPARING' | 'READY' | 'MISSING';
export interface PdfQueueDependencies {
  read: (id: number, variant: InvoicePdfVariant) => Promise<{ status: string }>;
  build: (invoice: InvoiceResponse) => Promise<Blob>;
  upload: (
    blob: Blob,
    invoice: InvoiceResponse,
    variant: InvoicePdfVariant,
    signal?: AbortSignal,
  ) => Promise<Uploaded>;
  attach: (
    invoice: InvoiceResponse,
    variant: InvoicePdfVariant,
    upload: Uploaded,
    signal?: AbortSignal,
  ) => Promise<unknown>;
  delay: (ms: number) => Promise<void>;
}
// A single worker covers manual actions, issue/cancel and catch-up. It never rejects
// into an issue/cancel mutation, and an attached original is never rewritten.
export class InvoicePdfQueue {
  private tail = Promise.resolve();
  private epoch = 0;
  private controller = new AbortController();
  reset() {
    this.epoch++;
    this.controller.abort();
    this.controller = new AbortController();
    this.jobs.clear();
    this.uploaded.clear();
    this.states.clear();
    this.listeners.forEach((listener) => listener());
  }
  private jobs = new Map<string, Promise<boolean>>();
  private uploaded = new Map<string, Uploaded>();
  private states = new Map<string, PdfState>();
  private listeners = new Set<() => void>();
  constructor(private deps: PdfQueueDependencies) {}
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  status = (id: number, variant: InvoicePdfVariant) => this.states.get(`${id}:${variant}`);
  private update(key: string, state: PdfState) {
    this.states.set(key, state);
    this.listeners.forEach((listener) => listener());
  }
  ensure(savedInvoice: InvoiceResponse, variant: InvoicePdfVariant): Promise<boolean> {
    const key = `${savedInvoice.id}:${variant}`;
    const epoch = this.epoch;
    const signal = this.controller.signal;
    const active = () => epoch === this.epoch && !signal.aborted;
    const existing = this.jobs.get(key);
    if (existing) return existing;
    // Snapshot from saved API data only. Form edits cannot race lazy PDF assets.
    const invoice = structuredClone(savedInvoice);
    invoice.status = variant === 'ORIGINAL' ? 'ISSUED' : 'CANCELLED';
    this.update(key, 'PREPARING');
    const job = this.tail.then(async () => {
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          if (!active()) return false;
          if ((await this.deps.read(invoice.id, variant)).status !== 'READY') {
            if (!active()) return false;
            let upload = this.uploaded.get(key);
            if (!upload) {
              const blob = await this.deps.build(invoice);
              if (!active()) return false;
              upload = await this.deps.upload(blob, invoice, variant, signal);
              if (!active()) return false;
              this.uploaded.set(key, upload);
            }
            if (!active()) return false;
            await this.deps.attach(invoice, variant, upload, signal);
          }
          if (!active()) return false;
          this.uploaded.delete(key);
          this.update(key, 'READY');
          return true;
        } catch (error) {
          if (!active()) return false;
          const status = (error as { response?: { status?: number } })?.response?.status;
          if (status === 400 || status === 409) this.uploaded.delete(key);
          if (attempt < 2) await this.deps.delay(1000 * (attempt + 1));
        }
      }
      this.update(key, 'MISSING');
      return false;
    });
    this.jobs.set(key, job);
    this.tail = job.then(() => {
      if (this.jobs.get(key) === job) this.jobs.delete(key);
    });
    return job;
  }
}

// Keep the intent before PUT/complete, so a lost complete response retries the same
// upload rather than leaving a second object. PDF bytes go directly to S3.
const uploads = new Map<
  string,
  {
    intentId: string;
    uploadUrl: string;
    headers: Record<string, string>;
    file: File;
    sha256: string;
    putDone: boolean;
    expiresAt: string;
  }
>();
export async function uploadSavedPdf(
  blob: Blob,
  invoice: InvoiceResponse,
  variant: InvoicePdfVariant,
  signal?: AbortSignal,
): Promise<Uploaded> {
  const checkActive = () => {
    if (signal?.aborted) throw new Error('PDF session ended');
  };
  checkActive();
  const key = `${invoice.id}:${variant}`;
  let state = uploads.get(key);
  if (state && Date.parse(state.expiresAt) <= Date.now()) {
    uploads.delete(key);
    state = undefined;
  }
  if (!state) {
    const bytes = await blob.arrayBuffer();
    const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));
    const sha256 = Array.from(digest, (b) => b.toString(16).padStart(2, '0')).join('');
    const file = new File(
      [bytes],
      `${invoice.invoiceNumber.replace(/[/\\]/g, '-')}${variant === 'CANCELLED' ? '-cancelled' : ''}.pdf`,
      { type: 'application/pdf' },
    );
    if (file.size > 10 * 1024 * 1024) throw new Error('PDF too large');
    checkActive();
    const intent = await createUploadIntent(
      {
        purpose: 'INVOICE_PDF',
        targetId: invoice.id,
        documentTag: variant,
        fileName: file.name,
        contentType: file.type,
        declaredSizeBytes: file.size,
        checksumSha256: sha256,
      },
      signal,
    );
    checkActive();
    state = {
      intentId: intent.intentId,
      uploadUrl: intent.uploadUrl,
      headers: intent.requiredHeaders,
      file,
      sha256,
      putDone: false,
      expiresAt: intent.expiresAt,
    };
    uploads.set(key, state);
  }
  if (!state.putDone) {
    const response = await fetch(state.uploadUrl, {
      method: 'PUT',
      headers: state.headers,
      body: state.file,
      signal,
    });
    checkActive();
    // A successful PUT with a lost response can return 412 on its write-once retry.
    // Complete+attach verify the actual stored object and its exact hash.
    if (!response.ok && response.status !== 412) throw new Error('PDF upload unavailable');
    state.putDone = true;
  }
  checkActive();
  await completeUploadIntent(state.intentId, signal);
  checkActive();
  uploads.delete(key);
  return { intentId: state.intentId, sha256: state.sha256 };
}
export const invoicePdfQueue = new InvoicePdfQueue({
  read: getInvoicePdf,
  build: async (invoice) => {
    const { buildInvoicePdf } = await import('./invoicePdf');
    return (await buildInvoicePdf(invoice)).output('blob');
  },
  upload: uploadSavedPdf,
  attach: attachInvoicePdf,
  delay: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
});

export let invoicePdfSession = 0;
export function resetInvoicePdfWork() {
  invoicePdfSession++;
  invoicePdfQueue.reset();
  uploads.clear();
}
